import React, { useState, useRef, useEffect } from "react"
import MenuGroup from "aldoo-ra/MenuGroup"
import Icon from "aldoo-ra/Icon"
import Value from "aldoo-ra/Value"

const Arrow = ({ className = "" }) => (
  <Icon id="arrow_menu_desktop" className="w-[54px] h-auto" />
)

const ArrowMenuDesktop = ({
  menuGroupName,
  onSelectionChange,
  preProcessItem,
  itemRender,
  onData,
  styleOverrides = {},
  initialSelectedIndex = 0,
  defaultSelected = null,
  enableScrollNavigation = false,
  hiddenItems = [],
  refreshKey = 0,
}) => {
  const [arrowPosition, setArrowPosition] = Value("arrowPosition", null)
  // Component state
  const [selectedIndex, setSelectedIndex] = useState(initialSelectedIndex)
  const [menuItemsLoaded, setMenuItemsLoaded] = useState(false)

  // Refs
  const menuRef = useRef(null)
  const itemRefs = useRef([])
  const menuItems = useRef([])
  const firstUpdate = useRef(true)
  const previousPositionRef = useRef(null) // Track last valid position

  // Get or create item ref
  const getItemRef = (index) => {
    if (!itemRefs.current[index]) {
      itemRefs.current[index] = React.createRef()
    }
    return itemRefs.current[index]
  }

  // Calculate position for a given index
  const calculatePositionForIndex = (index) => {
    if (!menuRef.current || index < 0 || !itemRefs.current[index]?.current)
      return null // Return null if can't calculate (handled elsewhere)

    const selectedItem = itemRefs.current[index].current
    const menuRect = menuRef.current.getBoundingClientRect()
    const itemRect = selectedItem.getBoundingClientRect()

    return itemRect.top - menuRect.top + itemRect.height / 2
  }

  // Update arrow position when selected index changes
  useEffect(() => {
    if (!menuRef.current || selectedIndex < 0) return

    const updateArrowPosition = () => {
      const position = calculatePositionForIndex(selectedIndex)

      if (position !== null) {
        // Only update if we have a valid position
        setArrowPosition(position)
        previousPositionRef.current = position
      }
      // If position is null, keep the previous position by not updating state
    }

    // Small delay to ensure DOM is updated
    const timer = setTimeout(updateArrowPosition, 50)

    // Update on resize
    window.addEventListener("resize", updateArrowPosition)
    return () => {
      window.removeEventListener("resize", updateArrowPosition)
      clearTimeout(timer)
    }
  }, [selectedIndex, menuItemsLoaded])

  // Notify parent of selection change
  useEffect(() => {
    if (
      onSelectionChange &&
      selectedIndex >= 0 &&
      menuItems.current[selectedIndex]
    ) {
      onSelectionChange(selectedIndex, menuItems.current[selectedIndex])
    }
  }, [selectedIndex, onSelectionChange])

  // Update selection when defaultSelected changes
  useEffect(() => {
    if (!defaultSelected || menuItems.current.length === 0) return

    const index = menuItems.current.findIndex(
      (item) => item.content?.options?.id === defaultSelected
    )

    if (index !== -1 && index !== selectedIndex) {
      setSelectedIndex(index)
    }
  }, [defaultSelected, menuItemsLoaded, selectedIndex])

  // Recalculate arrow positions after component mounts and menu items are loaded
  useEffect(() => {
    if (menuItemsLoaded && selectedIndex >= 0) {
      const position = calculatePositionForIndex(selectedIndex)

      // Only update if we get a valid position
      if (position !== null) {
        setArrowPosition(position)
        previousPositionRef.current = position
      }
    }
  }, [menuItemsLoaded, selectedIndex])


  return (
    <div
      ref={menuRef}
      className="fixed right-0 top-1/2 transform -translate-y-1/2 mr-[60px]"
      style={{ zIndex: 100 }}
    >
      <div className="relative">
        <MenuGroup
          key={`menu-${refreshKey}-${JSON.stringify(hiddenItems)}`}
          name={menuGroupName}
          styleOverrides={{
            menu_item: "my-0 py-0",
          }}
          skipRootItem
          renderAs="vlist"
          preProcessItem={preProcessItem}
          onData={(payload) => {
            const items = payload?.data[0]?.content?.items
            if (!items || !items.length) return

            // Filter out hidden items
            const filteredItems = items.filter(item =>
              !hiddenItems.includes(item.content?.options?.id)
            )

            menuItems.current = [...filteredItems]

            // Set initial selection if defaultSelected is provided
            if (defaultSelected && firstUpdate.current) {
              const index = filteredItems.findIndex(
                (item) => item.content?.options?.id === defaultSelected
              )

              if (index !== -1) {
                firstUpdate.current = false
                setSelectedIndex(index)
              }
            }

            setMenuItemsLoaded(true)

            if (onData) onData(payload)
          }}
          menuItemRender={(item, index) => {
            const categoryId = item.options?.id
            
            // Check if this item should be hidden
            if (hiddenItems.includes(categoryId)) {
              return null
            }

            const isSelected = selectedIndex === index

            const itemContent = itemRender ? (
              itemRender(item, index, isSelected)
            ) : (
              <div
                className={`w-[250px] h-[70px] flex items-center justify-end text-right px-6 cursor-pointer
                ${isSelected ? "text-primary font-bold" : "text-gray_2"}
                transition-colors duration-200`}
              >
                <span className="block text-xl">{item.label}</span>
              </div>
            )

            return (
              <div
                key={`menu-item-${index}`}
                ref={getItemRef(index)}
                onClick={() => {
                  if (index !== selectedIndex) {
                    setSelectedIndex(index)
                  }
                }}
                data-category={categoryId}
                className="transition-all duration-300"
              >
                {itemContent}
              </div>
            )
          }}
        />

        {/* Arrow component */}
        <div
          className="text-primary absolute -right-40 -top-2 w-40 transform z-10"
          style={{
            // Only apply transform when we have a valid position, otherwise use previous valid position
            transform: `translateY(${arrowPosition !== null ? arrowPosition : (previousPositionRef.current || 0)}px)`,
            transition: "transform 300ms cubic-bezier(0.4, 0, 0.2, 1)",
            // Only apply transition when arrowPosition is a valid value
            ...(arrowPosition === null && { transition: 'none' }),
          }}
        >
          <Arrow />
        </div>
      </div>
    </div>
  )
}

export default ArrowMenuDesktop